import React, { useState, useEffect, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useParent } from '@/hooks/useParent';
import { createConversation, sendMessage } from '@/services/messageService';
import { useAuth } from '@/hooks/useAuth';
import { ArrowLeft, Loader2, SmilePlus } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));
import { EmojiClickData } from 'emoji-picker-react';

export const CreateMessageForm: React.FC = () => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const { parent } = useParent();
  const { user } = useAuth();
  const navigate = useNavigate();
  const messageRef = React.useRef<HTMLTextAreaElement>(null);
  
  const isValidUUID = (value: string) => /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!parent || !user || !title.trim() || !message.trim()) return;
    if (!isValidUUID(parent.family_id) || !isValidUUID(parent.organization_id)) {
      console.error('Invalid family_id or organization_id:', parent.family_id, parent.organization_id);
      setSending(false);
      return;
    }

    try {
      setSending(true);
      
      // Create a new conversation (correct parameter order)
      const conversation = await createConversation(
        parent.organization_id,
        parent.family_id,
        title
      );
      
      // Send the first message in the conversation
      await sendMessage(
        conversation.id,
        parent.id,
        'parent',
        message,
        parent.organization_id
      );
      
      // Navigate to the new conversation
      navigate(`/parent-portal/messages/${conversation.id}`);
    } catch (error) {
      console.error('Error creating conversation:', error);
      setSending(false);
    }
  };
  
  const handleBack = () => {
    navigate('/parent-portal/messages');
  };
  
  // Insert emoji at cursor position in textarea
  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji;
    const cursorPosition = messageRef.current?.selectionStart || message.length;
    const updatedMessage =
      message.substring(0, cursorPosition) +
      emoji +
      message.substring(cursorPosition);
    setMessage(updatedMessage);
    setTimeout(() => {
      if (messageRef.current) {
        messageRef.current.focus();
        messageRef.current.selectionStart = cursorPosition + emoji.length;
        messageRef.current.selectionEnd = cursorPosition + emoji.length;
      }
    }, 10);
  };
  
  return (
    <div className="container max-w-2xl mx-auto px-4 py-8">
      <div className="mb-6">
        <Button variant="ghost" onClick={handleBack} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Messages
        </Button>
        <h1 className="text-2xl font-bold">New Message</h1>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Start a conversation</CardTitle>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Subject</Label>
              <Input
                id="title"
                placeholder="Enter a subject for your message"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <div className="flex items-center gap-2">
                <Textarea
                  id="message"
                  ref={messageRef}
                  placeholder="Type your message here..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  required
                  className="flex-1 resize-none"
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10"
                      title="Add emoji"
                    >
                      <SmilePlus className="h-5 w-5" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-auto" align="end">
                    <Suspense fallback={<div style={{ width: 300, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading…</div>}>
                      <EmojiPicker onEmojiClick={handleEmojiClick} width={300} height={400} />
                    </Suspense>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </CardContent>
          
          <CardFooter>
            <Button 
              type="submit" 
              disabled={sending || !title.trim() || !message.trim() || !parent}
              className="w-full"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default CreateMessageForm; 